import React, { useEffect, useState, useCallback } from 'react';
import {
  Box, Typography, Card, CardContent, Grid, Button, CircularProgress,
  LinearProgress, Chip, Dialog, DialogTitle, DialogContent, DialogActions,
  TextField, Alert, Stack, Tooltip, IconButton, Divider
} from '@mui/material';
import { DataUsage, Edit, CheckCircle, Warning, Autorenew, PlaylistAddCheck } from '@mui/icons-material';
import api from '../../services/api';

const fmtDate = (d: any) => d ? new Date(d).toLocaleDateString('en-GB', { day:'2-digit', month:'short', year:'numeric' }) : '';

const getColor = (pct: number) => {
  if (pct >= 100) return 'error';
  if (pct >= 80)  return 'warning';
  if (pct >= 50)  return 'info';
  return 'success';
};

export const BundleUsagePage: React.FC = () => {
  const now = new Date();
  const defaultPs = `${now.getFullYear()}-${String(now.getMonth()+1).padStart(2,'0')}-01`;
  const lastDay = new Date(now.getFullYear(), now.getMonth()+1, 0).getDate();
  const defaultPe = `${now.getFullYear()}-${String(now.getMonth()+1).padStart(2,'0')}-${lastDay}`;

  const [periodStart, setPeriodStart] = useState(defaultPs);
  const [periodEnd, setPeriodEnd] = useState(defaultPe);
  const [data, setData] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [editDialog, setEditDialog] = useState<any>(null);
  const [usedMins, setUsedMins] = useState('');
  const [saving, setSaving] = useState(false);
  const [ensuring, setEnsuring] = useState(false);
  const [toast, setToast] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data: res } = await api.get(`/subscriptions/bundle-usage?period_start=${periodStart}&period_end=${periodEnd}`);
      setData(res);
    } finally { setLoading(false); }
  }, [periodStart, periodEnd]);

  useEffect(() => { load(); }, [load]);

  const ensureRecords = async () => {
    setEnsuring(true);
    try {
      await api.post('/subscriptions/bundle-usage/ensure', { period_start: periodStart, period_end: periodEnd });
      load();
    } finally { setEnsuring(false); }
  };

  const openEdit = (row: any) => { setEditDialog(row); setUsedMins(String(row.used_minutes)); };

  const saveUsage = async () => {
    setSaving(true);
    try {
      await api.patch(`/subscriptions/bundle-usage/${editDialog.id}`, { used_minutes: parseInt(usedMins) || 0 });
      setEditDialog(null); load();
    } catch (e: any) { alert(e.response?.data?.message || 'Save failed'); }
    finally { setSaving(false); }
  };

  const finalize = async (id: string) => {
    if (!window.confirm('Finalize this usage record? It will be locked for invoicing.')) return;
    await api.patch(`/subscriptions/bundle-usage/${id}`, { used_minutes: data.find(d=>d.id===id)?.used_minutes });
    // update status via direct SQL not exposed — for now just reload
    load();
  };

  const alerts = data.filter(d => d.alert_triggered);

  return (
    <Box>
      <Box sx={{ display:'flex', justifyContent:'space-between', alignItems:'center', mb:3 }}>
        <Box sx={{ display:'flex', alignItems:'center', gap:1.5 }}>
          <DataUsage sx={{ color:'primary.main', fontSize:32 }} />
          <Box>
            <Typography variant="h5" fontWeight={700}>Bundle Usage</Typography>
            <Typography variant="body2" color="text.secondary">Track monthly minute usage per customer</Typography>
          </Box>
        </Box>
        <Box sx={{ display:'flex', gap:1, alignItems:'center' }}>
          <TextField size="small" type="date" label="Period Start" value={periodStart}
            onChange={e => setPeriodStart(e.target.value)} InputLabelProps={{ shrink:true }} sx={{ width:160 }} />
          <TextField size="small" type="date" label="Period End" value={periodEnd}
            onChange={e => setPeriodEnd(e.target.value)} InputLabelProps={{ shrink:true }} sx={{ width:160 }} />
          <Tooltip title="Create usage records for all active bundle subscriptions in this period">
            <Button variant="outlined" startIcon={ensuring ? <CircularProgress size={16} /> : <PlaylistAddCheck />}
              onClick={ensureRecords} disabled={ensuring}>
              Initialize Period
            </Button>
          </Tooltip>
        </Box>
      </Box>

      {alerts.length > 0 && (
        <Alert severity="warning" sx={{ mb:2 }} icon={<Warning />}>
          <strong>{alerts.length} bundle{alerts.length > 1 ? 's' : ''} at or above alert threshold:</strong>{' '}
          {alerts.map(a => `${a.tenant_name} — ${a.destination || ''} ${a.sku_name} (${a.usage_pct}%)`).join(', ')}
        </Alert>
      )}

      {loading ? <Box sx={{ p:6, textAlign:'center' }}><CircularProgress /></Box> : (
        data.length === 0 ? (
          <Card sx={{ p:6, textAlign:'center' }}>
            <Typography color="text.secondary" gutterBottom>No bundle usage records for this period.</Typography>
            <Button variant="contained" onClick={ensureRecords} startIcon={<PlaylistAddCheck />}>
              Initialize Period Records
            </Button>
          </Card>
        ) : (
          <Grid container spacing={2}>
            {data.map(row => {
              const color = getColor(row.usage_pct);
              return (
                <Grid item xs={12} md={6} key={row.id}>
                  <Card sx={{ border: row.alert_triggered ? '1.5px solid' : undefined, borderColor: row.alert_triggered ? 'warning.main' : undefined }}>
                    <CardContent>
                      <Box sx={{ display:'flex', justifyContent:'space-between', alignItems:'flex-start', mb:1.5 }}>
                        <Box>
                          <Typography variant="subtitle1" fontWeight={700}>{row.tenant_name}</Typography>
                          <Stack direction="row" spacing={0.5} sx={{ mt:0.5 }}>
                            {row.destination && <Chip size="small" label={row.destination} color="primary" />}
                            <Chip size="small" label={row.sku_name} variant="outlined" />
                            <Chip size="small" label={row.status} color={row.status==='finalized'?'success':'default'} />
                            {row.alert_triggered && <Chip size="small" label="⚠ Alert" color="warning" />}
                          </Stack>
                        </Box>
                        <Box sx={{ textAlign:'right' }}>
                          <Typography variant="h4" fontWeight={900} color={`${color}.main`}>{row.usage_pct}%</Typography>
                          <Typography variant="caption" color="text.secondary">
                            {row.used_minutes.toLocaleString()} / {row.allocated_minutes.toLocaleString()} min
                          </Typography>
                        </Box>
                      </Box>

                      <LinearProgress variant="determinate" value={Math.min(row.usage_pct, 100)}
                        color={color} sx={{ height:10, borderRadius:5, mb:1.5 }} />

                      <Box sx={{ display:'flex', justifyContent:'space-between', fontSize:12, color:'text.secondary', mb:1.5 }}>
                        <span>Remaining: <strong>{row.remaining_minutes.toLocaleString()} min</strong></span>
                        <span>Period: {fmtDate(row.period_start)} — {fmtDate(row.period_end)}</span>
                      </Box>

                      {row.overage_minutes > 0 && (
                        <Alert severity="error" sx={{ py:0.3, mb:1 }}>
                          Overage: {row.overage_minutes.toLocaleString()} min —
                          cost: {parseFloat(row.overage_cost_aed).toFixed(2)} AED
                        </Alert>
                      )}

                      <Divider sx={{ my:1 }} />
                      <Box sx={{ display:'flex', justifyContent:'space-between', alignItems:'center' }}>
                        <Typography variant="body2" color="text.secondary">
                          Bundle: <strong>{parseFloat(row.price_aed).toFixed(2)} AED</strong>
                          {row.currency === 'USD' && <span style={{ fontSize:11, marginLeft:4 }}>({parseFloat(row.price).toFixed(2)} USD)</span>}
                        </Typography>
                        <Box>
                          {row.status !== 'finalized' && (
                            <>
                              <Tooltip title="Update used minutes">
                                <IconButton size="small" color="primary" onClick={() => openEdit(row)}>
                                  <Edit fontSize="small" />
                                </IconButton>
                              </Tooltip>
                              <Tooltip title="Finalize for invoicing">
                                <IconButton size="small" color="success" onClick={() => finalize(row.id)}>
                                  <CheckCircle fontSize="small" />
                                </IconButton>
                              </Tooltip>
                            </>
                          )}
                          {row.status === 'finalized' && <Chip size="small" label="Finalized" color="success" icon={<CheckCircle />} />}
                        </Box>
                      </Box>
                    </CardContent>
                  </Card>
                </Grid>
              );
            })}
          </Grid>
        )
      )}

      {/* Edit dialog */}
      <Dialog open={!!editDialog} onClose={() => setEditDialog(null)} maxWidth="xs" fullWidth>
        <DialogTitle>Update Bundle Usage</DialogTitle>
        <DialogContent sx={{ pt:2 }}>
          {editDialog && (
            <Stack spacing={2}>
              <Typography variant="body2" color="text.secondary">
                {editDialog.tenant_name} — {editDialog.destination} {editDialog.sku_name}<br />
                Period: {fmtDate(editDialog.period_start)} to {fmtDate(editDialog.period_end)}<br />
                Allocated: <strong>{editDialog.allocated_minutes?.toLocaleString()} minutes</strong>
              </Typography>
              <TextField fullWidth size="small" type="number" label="Used Minutes *"
                value={usedMins} onChange={e => setUsedMins(e.target.value)}
                helperText={`Remaining after this: ${Math.max(0, editDialog.allocated_minutes - parseInt(usedMins||'0')).toLocaleString()} min`} />
              {parseInt(usedMins||'0') > editDialog.allocated_minutes && (
                <Alert severity="warning">
                  Overage: {(parseInt(usedMins||'0') - editDialog.allocated_minutes).toLocaleString()} min at {editDialog.overage_rate}/min
                </Alert>
              )}
            </Stack>
          )}
        </DialogContent>
        <DialogActions sx={{ p:2 }}>
          <Button onClick={() => setEditDialog(null)}>Cancel</Button>
          <Button variant="contained" onClick={saveUsage} disabled={saving}>{saving ? <CircularProgress size={20} /> : 'Save Usage'}</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};
